<script lang="ts">
    import { onMount, untrack } from "svelte";
    import { Vec2, css } from "ceyper-common";

    type Props = {
        width: number;
        height: number;
        container: HTMLDivElement;
        padding: Vec2;
    }

    let {
        width,
        height,
        container = $bindable(undefined),
        padding = Vec2.c(32, 32),
    } = $props();

    let pan = $state({ x: 0, y: 0 });
    let zoomScale = $state(1);
    let baseScale = $state(1);

    const zoomLevels = [
        1,
        2,
        3,
        4,
        6,
        8,
        12,
        16,
        32,
        48,
        64,
        128,
        256,
        512,
        1024,
    ];
    let zoomIndex = $state(zoomLevels.indexOf(1));

    export function startPan(event: { clientX: number; clientY: number }) {
        let startClient = Vec2.c(event.clientX, event.clientY);
        let startPan = Vec2.clone(pan);

        function panMove(event: MouseEvent) {
            pan = {
                x: startPan.x + event.clientX - startClient.x,
                y: startPan.y + event.clientY - startClient.y,
            };
        }

        function stopPan() {
            window.removeEventListener("pointermove", panMove);
        }

        window.addEventListener("pointermove", panMove);

        return stopPan;
    }

    function centerOnMouse(event: { clientX: number; clientY: number }) {
        const pos = Vec2.fromBoundsPosition(container);
        return Vec2.round(Vec2.sub(Vec2.sub(Vec2.fromEvent(event), pos), pan));
    }

    function zoom(center: Vec2, delta: number) {
        untrack(() => {
            zoomIndex = Math.clamp(zoomIndex + (delta > 0 ? -1 : 1), 0, zoomLevels.length - 1);
            let ds = zoomLevels[zoomIndex] / zoomScale;

            // Scale around the center
            pan = Vec2.round(Vec2.sub(pan, Vec2.scale(center, ds - 1)));
            zoomScale = zoomLevels[zoomIndex];
        });
    }

    export function zoomIn(event: { clientX: number; clientY: number }) {
        console.log("zoom in");
        zoom(centerOnMouse(event), -1);
    }

    export function zoomOut(event: { clientX: number; clientY: number }) {
        console.log("zoom out")
        zoom(centerOnMouse(event), 1);
    }

    export function zoomInCenter() {
        const bounds = Vec2.fromBoundsSize(container);
        const center = Vec2.round(Vec2.sub(Vec2.c(bounds.x / 2, bounds.y / 2), pan));
        zoom(center, -1);
    }

    export function zoomOutCenter() {
        const bounds = Vec2.fromBoundsSize(container);
        const center = Vec2.round(Vec2.sub(Vec2.c(bounds.x / 2, bounds.y / 2), pan));
        zoom(center, 1);
    }

    $effect(() => {
        if (width && height && container) {
            centerCamera();
        }
    });

    export function centerCamera() {
        untrack(() => {
            const bounds = Vec2.fromBoundsSize(container);
            console.log(bounds.x, bounds.y);
            resetZoom(bounds);

            const scaledImage = Vec2.scale({ x: width, y: height }, baseScale * zoomScale);
            pan = Vec2.scale(Vec2.sub(bounds, scaledImage), 0.5);
        });
    }

    function resetZoom(bounds: Vec2) {
        baseScale = 1 / devicePixelRatio;

        // find the zoom level that fits the container
        const maxScaleVec = Vec2.div(Vec2.sub(bounds, Vec2.scale(padding, 2 * baseScale)), { x: width * baseScale,  y: height * baseScale });
        const maxScale = Math.min(maxScaleVec.x, maxScaleVec.y);
        const index = Math.max(zoomLevels.findIndex((level) => level >= maxScale) - 1, 0);

        zoomIndex = Math.max(index - 1, 0);
        zoomScale = zoomLevels[index];
    }

    onMount(() => {
        centerCamera();
    });
</script>

<!----------------------------------------------------------------------------->

<div
    class="container"
    bind:this={container}
    style="--scale: {baseScale * zoomScale};"
    use:css={Vec2.toCss(pan)}
>
    <slot state={{ pan: pan, scale: zoomScale}} />
</div>

<!----------------------------------------------------------------------------->

<style>
    .container {
        position: relative;
        width: 100%;
        height: 100%;
        isolation: isolate;
    }

    .container > :global(*) {
        position: absolute;

        top: var(--y);
        left: var(--x);
        scale: var(--scale);
        transform-origin: 0 0;
    }
</style>
